import { hashKey, skipToken, useQueries, type UseQueryOptions } from "@tanstack/react-query";

import type { LayerDefinition } from "../../../models/mapConfiguration";
import type { FilteredLayerItems } from "./queries/layerClientFilters";
import type { LayerItem } from "./queries/layerRequests";

import { QUERY_KEYS } from "../../../api/queries";
import { isLayerVisible } from "../layers/layer";
import { filterLayerItems } from "./queries/layerClientFilters";
import { buildLayerRequest } from "./queries/layerRequests";

export interface LayerResult extends FilteredLayerItems {
	isLoading: boolean;
	isError: boolean;
}

export type LayerResults = ReadonlyMap<string, LayerResult>;

const NO_DATA: LayerItem[] = [];
const filteredCache = new WeakMap<LayerItem[], WeakMap<LayerDefinition, FilteredLayerItems>>();

export function useLayerQueries(layers: LayerDefinition[]): LayerResults {
	const { queries, slots } = uniqueQueries(layers.map(layerQueryOptions));
	const results = useQueries({ queries });

	return new Map(
		layers.map((layer, index) => {
			const query = results[slots[index]];
			const filtered = filterLayerItemsCached(layer, query.data ?? NO_DATA);
			return [layer.id, { ...filtered, isLoading: query.isFetching, isError: query.isError }];
		}),
	);
}

function layerQueryOptions(layer: LayerDefinition): UseQueryOptions<LayerItem[]> {
	const request = isLayerVisible(layer) ? buildLayerRequest(layer) : undefined;
	if (!request) return { queryKey: [QUERY_KEYS.Maps.MapLayer, "idle", layer.id], queryFn: skipToken };
	return { queryKey: [QUERY_KEYS.Maps.MapLayer, request.queryId, request.params], queryFn: request.load };
}

// useQueries warns on duplicate keys, so layers with identical requests share one entry.
function uniqueQueries(options: UseQueryOptions<LayerItem[]>[]) {
	const slotByHash = new Map<string, number>();
	const queries: UseQueryOptions<LayerItem[]>[] = [];
	const slots = options.map((option) => {
		const hash = hashKey(option.queryKey);
		if (!slotByHash.has(hash)) {
			slotByHash.set(hash, queries.length);
			queries.push(option);
		}
		return slotByHash.get(hash);
	});
	return { queries, slots };
}

function filterLayerItemsCached(layer: LayerDefinition, data: LayerItem[]) {
	let byLayer = filteredCache.get(data);
	if (!byLayer) filteredCache.set(data, (byLayer = new WeakMap()));

	let filtered = byLayer.get(layer);
	if (!filtered) byLayer.set(layer, (filtered = filterLayerItems(layer, data)));
	return filtered;
}
